// HRMS — Employees + Directory.
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import hr from '../api';
import { PageHeader, Btn, Drawer, Input, Select, Textarea, Table, Pill, Avatar, fmtDate, statusTone } from '../ui';

const STATUSES = ['active', 'probation', 'notice', 'terminated', 'on_leave'];

export function Employees({ companyId }) {
  const [rows, setRows] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({});
  const reload = async () => setRows(await hr.get('/employees', { company_id: companyId }));
  useEffect(() => {
    if (!companyId) return;
    reload();
    (async () => {
      setDepartments(await hr.get('/departments', { company_id: companyId }));
      setDesignations(await hr.get('/designations', { company_id: companyId }));
    })();
  }, [companyId]);

  const newEmp = () => {
    setEdit(null);
    setForm({ company_id: companyId, employment_type: 'full_time', status: 'active', probation_months: 6,
              education: [], experience: [], skills: [], documents: [], certifications: [], nationality: 'Indian' });
    setOpen(true);
  };

  const save = async () => {
    try {
      if (!form.first_name) return toast.error('First name required');
      const payload = { ...form, company_id: companyId };
      if (payload.ctc_annual) payload.ctc_annual = Number(payload.ctc_annual);
      if (edit) await hr.put(`/employees/${edit.id}`, payload);
      else await hr.post('/employees', payload);
      toast.success('Saved');
      setOpen(false); reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  return (
    <div data-testid="hr-employees">
      <PageHeader title="Employees" subtitle={`${rows.length} on rolls`}
        right={<Btn variant="accent" onClick={newEmp} data-testid="hr-new-employee">+ New Employee</Btn>} />
      <Table
        columns={[
          { key: 'avatar', label: '', render: (r) => <Avatar name={r.full_name} photo={r.photo_base64} /> },
          { key: 'emp_code', label: 'Code', render: (r) => <span className="font-mono text-xs text-white/55">{r.emp_code}</span> },
          { key: 'full_name', label: 'Name', render: (r) => (<div><div className="text-white font-medium">{r.full_name}</div><div className="text-xs text-white/40">{r.designation_name || ''}</div></div>) },
          { key: 'department_name', label: 'Department' },
          { key: 'work_email', label: 'Email', render: (r) => <span className="text-xs text-white/60">{r.work_email || r.personal_email || '—'}</span> },
          { key: 'employment_type', label: 'Type', render: (r) => <Pill tone="blue">{r.employment_type?.replace('_', ' ')}</Pill> },
          { key: 'status', label: 'Status', render: (r) => <Pill tone={statusTone(r.status)}>{r.status}</Pill> },
          { key: 'joining_date', label: 'Joined', render: (r) => fmtDate(r.joining_date) },
          { key: 'actions', label: '', render: (r) => (<button className="text-xs text-white/55 hover:text-white" onClick={() => { setEdit(r); setForm({ ...r, company_id: companyId }); setOpen(true); }}>Edit</button>) },
        ]}
        rows={rows}
        empty="No employees yet."
        testidPrefix="employee"
      />

      <Drawer open={open} onClose={() => setOpen(false)} title={edit ? `Edit ${edit.full_name}` : 'New Employee'} width="max-w-5xl">
        <div className="grid grid-cols-3 gap-3 mb-4">
          <Input label="First Name *" value={form.first_name || ''} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="emp-first-name" />
          <Input label="Last Name" value={form.last_name || ''} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
          <Input label="Display Name" value={form.display_name || ''} onChange={(e) => setForm({ ...form, display_name: e.target.value })} />
          <Input label="Work Email" type="email" value={form.work_email || ''} onChange={(e) => setForm({ ...form, work_email: e.target.value })} />
          <Input label="Personal Email" type="email" value={form.personal_email || ''} onChange={(e) => setForm({ ...form, personal_email: e.target.value })} />
          <Input label="Phone" value={form.phone || ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          <Select label="Department" value={form.department_id || ''} onChange={(e) => setForm({ ...form, department_id: e.target.value })}>
            <option value="">— Select —</option>
            {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </Select>
          <Select label="Designation" value={form.designation_id || ''} onChange={(e) => setForm({ ...form, designation_id: e.target.value })}>
            <option value="">— Select —</option>
            {designations.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </Select>
          <Select label="Employment Type" value={form.employment_type || 'full_time'} onChange={(e) => setForm({ ...form, employment_type: e.target.value })}>
            <option value="full_time">Full-time</option><option value="part_time">Part-time</option>
            <option value="contract">Contract</option><option value="intern">Intern</option>
          </Select>
          <Select label="Status" value={form.status || 'active'} onChange={(e) => setForm({ ...form, status: e.target.value })}>
            {STATUSES.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
          </Select>
          <Input label="Joining Date" type="date" value={(form.joining_date || form.joining_date_iso || '').slice(0, 10)} onChange={(e) => setForm({ ...form, joining_date: e.target.value })} />
          <Input label="CTC (annual)" type="number" value={form.ctc_annual ?? 0} onChange={(e) => setForm({ ...form, ctc_annual: e.target.value })} />
          <Select label="Gender" value={form.gender || ''} onChange={(e) => setForm({ ...form, gender: e.target.value })}>
            <option value="">—</option><option value="male">Male</option><option value="female">Female</option><option value="other">Other</option>
          </Select>
          <Input label="Date of Birth" type="date" value={(form.dob || '').slice(0, 10)} onChange={(e) => setForm({ ...form, dob: e.target.value })} />
          <Input label="Aadhaar" value={form.aadhaar || ''} onChange={(e) => setForm({ ...form, aadhaar: e.target.value })} />
          <Input label="PAN" value={form.pan || ''} onChange={(e) => setForm({ ...form, pan: e.target.value })} />
          <Input label="Bank Account" value={form.bank_account || ''} onChange={(e) => setForm({ ...form, bank_account: e.target.value })} />
          <Input label="IFSC" value={form.bank_ifsc || ''} onChange={(e) => setForm({ ...form, bank_ifsc: e.target.value })} />
        </div>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <Textarea label="Current Address" value={form.current_address || ''} onChange={(e) => setForm({ ...form, current_address: e.target.value })} />
          <Textarea label="Permanent Address" value={form.permanent_address || ''} onChange={(e) => setForm({ ...form, permanent_address: e.target.value })} />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Input label="Emergency Contact Name" value={form.emergency_name || ''} onChange={(e) => setForm({ ...form, emergency_name: e.target.value })} />
          <Input label="Emergency Phone" value={form.emergency_phone || ''} onChange={(e) => setForm({ ...form, emergency_phone: e.target.value })} />
          <Input label="Relation" value={form.emergency_relation || ''} onChange={(e) => setForm({ ...form, emergency_relation: e.target.value })} />
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Btn variant="ghost" onClick={() => setOpen(false)}>Cancel</Btn>
          <Btn variant="accent" onClick={save} data-testid="emp-save">Save Employee</Btn>
        </div>
      </Drawer>
    </div>
  );
}

export function Directory({ companyId }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { if (companyId) hr.get('/directory', { company_id: companyId }).then(setRows); }, [companyId]);
  return (
    <div data-testid="hr-directory">
      <PageHeader title="Directory" subtitle={`${rows.length} colleagues — searchable employee map`} />
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {!rows.length ? <p className="text-white/40 text-sm italic">No employees.</p> :
          rows.map((e) => (
            <div key={e.id} className="bg-white/[0.03] border border-white/10 rounded-xl p-4 flex gap-3 items-center hover:bg-white/[0.05] transition">
              <Avatar name={e.full_name} photo={e.photo_base64} size={48} />
              <div className="min-w-0">
                <div className="text-white truncate font-medium">{e.full_name}</div>
                <div className="text-xs text-white/55 truncate">{e.designation_name || '—'}</div>
                <div className="text-xs text-white/40 truncate">{e.department_name || ''}</div>
                <div className="text-[10px] text-white/35 truncate mt-1">{e.work_email || e.phone}</div>
              </div>
            </div>
          ))
        }
      </div>
    </div>
  );
}
